import { type ChatMessageItem } from '../../components/chat/ChatMessage';
import { type SuggestedAction } from '../../types/ai';

export const LANGUAGE_OPTIONS = [
  { code: 'sv', flag: '🇸🇪', label: 'Svenska' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'da', flag: '🇩🇰', label: 'Dansk' },
  { code: 'no', flag: '🇳🇴', label: 'Norsk' },
  { code: 'fi', flag: '🇫🇮', label: 'Suomi' },
  { code: 'nl', flag: '🇳🇱', label: 'Nederlands' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'it', flag: '🇮🇹', label: 'Italiano' },
] as const;

export const LANG_TO_SIMULATED_MARKET: Record<string, { hostname: string; currency: string }> = {
  sv: { hostname: 'www.careofcarl.se', currency: 'SEK' },
  en: { hostname: 'www.careofcarl.com', currency: 'EUR' },
  de: { hostname: 'www.careofcarl.de', currency: 'EUR' },
  fr: { hostname: 'www.careofcarl.fr', currency: 'EUR' },
  da: { hostname: 'www.careofcarl.dk', currency: 'DKK' },
  no: { hostname: 'www.careofcarl.no', currency: 'NOK' },
  fi: { hostname: 'www.careofcarl.fi', currency: 'EUR' },
  nl: { hostname: 'www.careofcarl.nl', currency: 'EUR' },
  es: { hostname: 'www.careofcarl.es', currency: 'EUR' },
  it: { hostname: 'www.careofcarl.it', currency: 'EUR' },
};

export const WELCOME_SEED_BY_LANG: Record<string, string> = {
  sv: 'hej',
  en: 'hello',
  de: 'hallo',
  fr: 'bonjour',
  da: 'hej',
  no: 'hei',
  fi: 'hei',
  nl: 'hallo',
  es: 'hola',
  it: 'ciao',
};

export const WELCOME_SEED_FALLBACK = 'hej';

export type ChannelValue = 'playground';

export const createId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

export const WELCOME_MESSAGE = `Hej! Jag är en AI-assistent från Care of Carl.

Jag kan hjälpa dig med:
• Beställningar och orderstatus
• Leveranser och spårning
• Produkter och outfits
• Returer
• Allmän information om Care of Carl

Hur kan jag hjälpa dig idag?`;

export const WELCOME_SUGGESTED_ACTIONS: SuggestedAction[] = [
  {
    label: 'Orderstatus',
    value: 'Jag vill kolla status på min beställning',
    actionId: 'order_tracking',
    payload: {
      type: 'conversation_mode',
      mode: 'order_help',
      label: 'Orderstatus',
    },
  },
  {
    label: 'Leverans & spårning',
    value: 'Jag vill spåra min leverans',
    actionId: 'order_tracking',
    payload: {
      type: 'conversation_mode',
      mode: 'order_help',
      label: 'Leverans & spårning',
    },
  },
  {
    label: 'Returer',
    value: 'Jag har en fråga om returer',
    actionId: 'general.info.node.returns_and_exchanges',
    payload: {
      type: 'general_info_suggestion',
      nodeId: 'returns_and_exchanges',
      intent: 'returns_and_exchanges',
      label: 'Returer',
    },
  },
  {
    label: 'Outfit',
    value: 'Jag vill ha hjälp med outfit',
    actionId: 'outfit.menu.open',
  },
  {
    label: 'Senast inkommet',
    value: 'Senast inkommet',
    actionId: 'welcome.external.latest_arrivals',
    externalUrl: 'https://www.careofcarl.se/sv/senast-inkommet',
  },
  {
    label: 'Carl Magazine',
    value: 'Carl Magazine',
    actionId: 'welcome.external.carl_magazine',
    externalUrl: 'https://www.careofcarl.se/carlmagazine',
  },
  {
    label: 'The Passport',
    value: 'Jag vill veta mer om The Passport',
    actionId: 'general.info.node.passport',
    payload: {
      type: 'general_info_suggestion',
      nodeId: 'passport',
      intent: 'passport',
      label: 'The Passport',
    },
  },
  {
    label: 'Allmän info',
    value: 'Jag har en allmän fråga om Care of Carl',
    actionId: 'general',
    payload: {
      type: 'conversation_mode',
      mode: 'general_info',
      label: 'Allmän info',
    },
  },
];

export type SendMessageOptions = {
  suggestedAction?: SuggestedAction;
};

export type StreamStatus = {
  key: string;
  text: string;
};

export const STATUS_MIN_VISIBLE_MS = 1200;
export const STATUS_DUPLICATE_WINDOW_MS = 4_000;
export const IDLE_WARNING_MS = 5 * 60 * 1000;
export const IDLE_TIMEOUT_MS = 20 * 60 * 1000;

export const normalizeStreamStatus = (status: StreamStatus): StreamStatus => ({
  key: String(status.key ?? '').trim() || 'status',
  text: String(status.text ?? '').trim(),
});

export const pickSuggestedActionFields = (
  action?: SuggestedAction,
): SuggestedAction | undefined => {
  if (!action) {
    return undefined;
  }

  const label = String(action.label ?? '').trim();
  const value = String(action.value ?? '').trim();

  if (!label || !value) {
    return undefined;
  }

  return {
    label,
    value,
    ...(typeof action.actionId === 'string' && action.actionId.trim().length > 0
      ? { actionId: action.actionId.trim() }
      : {}),
    ...(typeof action.externalUrl === 'string' && action.externalUrl.trim().length > 0
      ? { externalUrl: action.externalUrl.trim() }
      : {}),
    ...(typeof action.intentType === 'string' && action.intentType.trim().length > 0
      ? { intentType: action.intentType.trim() }
      : {}),
    ...(typeof action.canonicalAction === 'string' && action.canonicalAction.trim().length > 0
      ? { canonicalAction: action.canonicalAction.trim() }
      : {}),
    ...(action.payload ? { payload: action.payload } : {}),
  };
};

export const getVisibleSuggestedActionLabel = (action?: SuggestedAction): string | undefined => {
  if (!action) return undefined;

  const rawLabel = String(action.label ?? '').trim();
  const rawValue = String(action.value ?? '').trim();
  const source = /^outfit_change:/i.test(rawLabel) ? rawValue : rawLabel || rawValue;
  const match = source.match(/^outfit_change:(.+)$/i);

  if (!match) {
    return source || undefined;
  }

  const cleaned = match[1].trim().replace(/[_-]+/g, ' ');
  return cleaned ? `Byt ${cleaned}` : 'Byt plagg';
};

export const createWelcomeChatMessage = (): ChatMessageItem => ({
  id: 'welcome',
  role: 'assistant',
  content: WELCOME_MESSAGE,
  createdAt: new Date().toISOString(),
  status: 'success',
  suggestedActions: WELCOME_SUGGESTED_ACTIONS,
});
